import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Drawer, Modal } from "antd";
import SelectInput from "../../atoms/SelectInput/SelectInput";
import Input from "../../atoms/Input/Input";
import AddButton from "../../atoms/AddButton/AddButton";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import "./Apply-Hours-Drawer.scss";
import Select from "react-select";
import { useDispatch, useSelector } from "react-redux";
import { getAllProject } from "../../../Redux/Apis/projectApi";
import moment from "moment";
import { showErrorToast, showSuccessToast } from "../../../app/utils/common/Toast";
import {
  checkPermission,
  hasAdminAccess,
  permissionType,
  splitDailyHoursNotifyEmails,
} from "../../../app/utils/common/Functions";
import {
  buildDailyHoursViewMailPlainText,
  buildDailyHoursViewMailSubject,
} from "../../../app/utils/common/dailyHoursMailto";
import { sendDailyHoursViewEmail } from "../../../app/utils/common/dailyHoursEmail";
import { createTaskAPI, getDailyWorkBoardCardsAPI, getWorkingHourAPI } from "../../../Redux/Apis/taskApi";
import Loader from "../../atoms/Loader/Loader";
import ErrorText from "../../atoms/Error/errorText";
import { addTask } from "../../../Redux/Slices/taskSlice";
import { formatDurationHoursMinutes } from "../../../app/utils/common/attendancePunchDisplay";
import {
  getLeaveBannerMessage,
  getPartialLeaveComplianceStatus,
  getPartialLeaveStatusLabel,
  getRequiredWorkingHours,
  isFullLeaveDay,
  isPartialLeaveDay,
} from "../../../app/utils/common/dailyWorkLeave";
import { isDailyWorkUpdatePending } from "../../../app/utils/common/dailyWorkGracePeriod";
import { isMonthlyAccessBlockedError } from "../../../app/utils/common/monthlyAccessBlockedError";
import MonthlyAccessRequestModal from "../MonthlyAccessRequestModal/MonthlyAccessRequestModal";

const DURATION_STEP_MINUTES = 5;
const MAX_DURATION_HOURS = 15;
const MAX_DURATION_MINUTES = MAX_DURATION_HOURS * 60;

const hourOptions = Array.from({ length: MAX_DURATION_HOURS }, (_, i) => {
  const h = i + 1;
  return {
    value: String(h),
    label: h === 1 ? "1 hour" : `${h} hours`,
  };
});

const minuteOptions = Array.from(
  { length: 60 / DURATION_STEP_MINUTES - 1 },
  (_, i) => {
    const m = (i + 1) * DURATION_STEP_MINUTES;
    return { value: String(m), label: `${m} min` };
  }
);

const toHourOption = (hours) =>
  hourOptions.find((o) => o.value === String(hours)) ?? null;

const toMinuteOption = (minutes) =>
  minuteOptions.find((o) => o.value === String(minutes)) ?? null;

const getTaskDurationMinutesFromFields = (task) => {
  const hours = task.durationHours?.value
    ? parseInt(task.durationHours.value, 10)
    : 0;
  const minutes = task.durationMinutes?.value
    ? parseInt(task.durationMinutes.value, 10)
    : 0;
  return hours * 60 + minutes;
};

const minutesToDurationParts = (totalMinutes) => {
  const rounded =
    Math.round(totalMinutes / DURATION_STEP_MINUTES) * DURATION_STEP_MINUTES;
  const clamped = Math.min(
    MAX_DURATION_MINUTES,
    Math.max(0, rounded)
  );
  const hours = Math.floor(clamped / 60);
  const minutes = clamped % 60;
  return {
    durationHours: toHourOption(hours),
    durationMinutes: toMinuteOption(minutes),
  };
};

const getMinuteOptionsForHours = (hours) => {
  const remaining = MAX_DURATION_MINUTES - hours * 60;
  return minuteOptions.filter(
    (o) => parseInt(o.value, 10) <= remaining
  );
};

const EMPTY_DAILY_EXTRAS = {
  inProgressTasks: "",
  plannedForTomorrow: "",
  issueBlocker: "",
  dependency: "",
  supportRequiredFrom: "",
  meetingsDiscussions: "",
  clientFeedback: "",
  deploymentNotes: "",
};

const EMPTY_TASK = {
  selectedProject: null,
  prjCategory: null,
  durationHours: null,
  durationMinutes: null,
  taskDescription: "",
  selectedDevQaCards: [],
  selectedInProgressCards: [],
  devQaDurationHours: null,
  devQaDurationMinutes: null,
  devQaOverReason: "",
  boardCardOptions: [],
  boardCardsLoading: false,
};


const isInternalProjectOption = (projectOption) =>
  projectOption?.projectType === "internal";

/** Internal + board member → board cards; other assignees on internal projects stay free-form. */
const usesBoardCardsDailyWork = (projectOption) =>
  isInternalProjectOption(projectOption) && projectOption?.isBoardMember === true;

const getDurationMinutesFromParts = (hoursPart, minutesPart) => {
  const hours = hoursPart?.value ? parseInt(hoursPart.value, 10) : 0;
  const minutes = minutesPart?.value ? parseInt(minutesPart.value, 10) : 0;
  return hours * 60 + minutes;
};

const getBoardActualMinutesForSelectedDevQa = (task) =>
  (task?.selectedDevQaCards || []).reduce(
    (sum, card) => sum + Math.round(Number(card.actualHours || 0) * 60),
    0
  );

const getCardPreviouslyLoggedMinutes = (card) =>
  Math.max(0, Math.round(Number(card?.previouslyLoggedMinutes || 0)));

const getCardRemainingBoardMinutes = (card) => {
  if (card?.remainingBoardMinutes != null) {
    return Math.max(0, Math.round(Number(card.remainingBoardMinutes)));
  }
  const boardMinutes = Math.round(Number(card?.actualHours || 0) * 60);
  return Math.max(0, boardMinutes - getCardPreviouslyLoggedMinutes(card));
};

const getPreviouslyLoggedMinutesForSelectedDevQa = (task) =>
  (task?.selectedDevQaCards || []).reduce(
    (sum, card) => sum + getCardPreviouslyLoggedMinutes(card),
    0
  );

const getRemainingBoardMinutesForSelectedDevQa = (task) =>
  (task?.selectedDevQaCards || []).reduce(
    (sum, card) => sum + getCardRemainingBoardMinutes(card),
    0
  );

const getInProgressCardMinutes = (card) =>
  getDurationMinutesFromParts(card?.inProgressHours, card?.inProgressMinutes);

const getInternalDevQaMinutes = (task) =>
  getDurationMinutesFromParts(task?.devQaDurationHours, task?.devQaDurationMinutes);

const getInternalInProgressMinutes = (task) =>
  (task?.selectedInProgressCards || []).reduce(
    (sum, card) => sum + getInProgressCardMinutes(card),
    0
  );

const getInternalTaskTotalMinutes = (task) =>
  getInternalDevQaMinutes(task) + getInternalInProgressMinutes(task);

const getBoardEstimatedCapMinutes = (card) => {
  const estimated = card?.estimatedHours;
  if (estimated == null || estimated === "") return null;
  const value = Number(estimated);
  if (Number.isNaN(value) || value < 0) return null;
  return Math.round(value * 60);
};

const getInProgressCardRemainingEstimatedMinutes = (card) => {
  if (card?.remainingEstimatedMinutes != null) {
    return Math.max(0, Math.round(Number(card.remainingEstimatedMinutes)));
  }
  const cap = getBoardEstimatedCapMinutes(card);
  if (cap == null) return null;
  return Math.max(0, cap - getCardPreviouslyLoggedMinutes(card));
};

const inProgressCardNeedsOverReason = (card) => {
  const cap = getBoardEstimatedCapMinutes(card);
  if (cap == null) return false;
  const total = getCardPreviouslyLoggedMinutes(card) + getInProgressCardMinutes(card);
  return total > cap;
};

const devQaDurationNeedsOverReason = (task) => {
  if (!(task?.selectedDevQaCards || []).length) return false;
  const entered = getInternalDevQaMinutes(task);
  const remaining = getRemainingBoardMinutesForSelectedDevQa(task);
  return entered > remaining;
};

const getTaskDurationMinutes = (task) => {
  if (task && usesBoardCardsDailyWork(task.selectedProject)) {
    const internalTotal = getInternalTaskTotalMinutes(task);
    if (internalTotal > 0) return internalTotal;
  }
  return getTaskDurationMinutesFromFields(task);
};

const getTaskDurationHours = (task) => getTaskDurationMinutes(task) / 60;

const getBoardTaskDisplayLabel = (card) => {
  if (card.ticketNo) return `${card.ticketNo}: ${card.title}`;
  if (card.title) return card.title;
  return String(card.label || "").replace(/^\[[^\]]+\]\s*/, "");
};

const toBoardTaskSelectOption = (card) => ({
  value: card.cardId ?? card.value,
  label: getBoardTaskDisplayLabel(card),
  cardId: card.cardId ?? card.value,
  listType: card.listType,
  listStatus: card.listStatus,
  actualHours: card.actualHours,
  estimatedHours: card.estimatedHours,
  actualHoursOverReason: card.actualHoursOverReason || "",
  previouslyLoggedMinutes: card.previouslyLoggedMinutes || 0,
  remainingBoardMinutes: card.remainingBoardMinutes,
  remainingEstimatedMinutes: card.remainingEstimatedMinutes,
  title: card.title,
  ticketNo: card.ticketNo,
  description: card.description || "",
  checklist: Array.isArray(card.checklist) ? card.checklist : [],
  projectName: card.projectName,
});

const cardsToSelectValue = (cards = []) => cards.map(toBoardTaskSelectOption);

const formatBoardCardOption = (card) => toBoardTaskSelectOption({
  ...card,
  label: getBoardTaskDisplayLabel(card),
});

const formatCardHoursLabel = (hours) => {
  const value = Number(hours);
  if (Number.isNaN(value)) return "0h";
  return Number.isInteger(value) ? `${value}h` : `${value.toFixed(2).replace(/\.?0+$/, "")}h`;
};

const escapeHtmlText = (value) =>
  String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");

const isBoardCardDescriptionEmpty = (html) => {
  const s = String(html ?? "").trim();
  if (!s) return true;
  if (s === "<p><br></p>" || s === "<p></p>") return true;
  return !s.replace(/<[^>]+>/g, "").trim();
};

/** Append board card description under the title when present. */
const formatBoardCardDescriptionHtml = (card) => {
  const desc = card?.description;
  if (isBoardCardDescriptionEmpty(desc)) return "";
  const raw = String(desc).trim();
  const looksLikeHtml = /<[a-z][\s\S]*>/i.test(raw);
  if (looksLikeHtml) return raw;
  return `<p>${escapeHtmlText(raw).replace(/\r\n|\r|\n/g, "<br>")}</p>`;
};

/** Completed checklist items for In-Progress daily work / email. */
const formatBoardCardDoneChecklistHtml = (card) => {
  const doneItems = (Array.isArray(card?.checklist) ? card.checklist : [])
    .filter((item) => item && item.checked && String(item.text || "").trim());
  if (!doneItems.length) return "";

  const itemsHtml = doneItems
    .map((item) => `<li>${escapeHtmlText(String(item.text).trim())}</li>`)
    .join("");
  return `<p><strong>Checklist done:</strong></p><ul>${itemsHtml}</ul>`;
};

const formatBoardCardTitleLineHtml = (card, suffixHtml, { includeDoneChecklist = false } = {}) => {
  const title =
    `${card.ticketNo ? `${escapeHtmlText(card.ticketNo)}: ` : ""}${escapeHtmlText(card.title)}`;
  return (
    `<p>${title}${suffixHtml || ""}</p>`
    + formatBoardCardDescriptionHtml(card)
    + (includeDoneChecklist ? formatBoardCardDoneChecklistHtml(card) : "")
  );
};

/** Include over-estimate reason in daily work / email HTML descriptions. */
const formatOverEstimateReasonHtml = (reason) => {
  const text = String(reason || "").trim();
  if (!text) return "";
  const withBreaks = escapeHtmlText(text).replace(/\r\n|\r|\n/g, "<br>");
  return `<p><strong>Over estimate:</strong> ${withBreaks}</p>`;
};

const buildInternalTaskDescription = (
  devQaCards = [],
  inProgressCards = [],
  devQaMinutes = 0,
  inProgressMinutes = 0,
  { devQaOverReason = "" } = {}
) => {
  const lines = [];
  if (devQaCards.length) {
    lines.push(
      `<p><strong>Dev QA Done (${formatDurationHoursMinutes(devQaMinutes)}):</strong></p>`
    );
    devQaCards.forEach((card) => {
      lines.push(
        formatBoardCardTitleLineHtml(
          card,
          ` — board ${formatCardHoursLabel(card.actualHours || 0)}`
        )
      );
    });
    const sharedReason = String(devQaOverReason || "").trim();
    if (sharedReason) {
      lines.push(formatOverEstimateReasonHtml(sharedReason));
    }
  }
  if (inProgressCards.length) {
    lines.push(
      `<p><strong>In-Progress (${formatDurationHoursMinutes(inProgressMinutes)}):</strong></p>`
    );
    inProgressCards.forEach((card) => {
      const cardMinutes = getInProgressCardMinutes(card);
      lines.push(
        formatBoardCardTitleLineHtml(
          card,
          ` — ${formatDurationHoursMinutes(cardMinutes)}`,
          { includeDoneChecklist: true }
        )
      );
      const reason = String(card.inProgressOverReason || "").trim();
      if (reason) {
        lines.push(formatOverEstimateReasonHtml(reason));
      }
    });
  }
  return lines.join("");
};

const splitBoardCardOptions = (options = []) => ({
  devQaCards: options.filter((card) => card.listType === "dev_qa_done"),
  inProgressCards: options.filter((card) => card.listType === "in_progress"),
});

const syncInternalTaskState = (task) => {
  const devQaCards = task.selectedDevQaCards || [];
  const inProgressCards = task.selectedInProgressCards || [];
  const devQaMinutes = getInternalDevQaMinutes(task);
  const inProgressMinutes = getInternalInProgressMinutes(task);
  return {
    ...task,
    taskDescription: buildInternalTaskDescription(
      devQaCards,
      inProgressCards,
      devQaMinutes,
      inProgressMinutes,
      { devQaOverReason: task.devQaOverReason || "" }
    ),
    durationHours: toHourOption(Math.floor((devQaMinutes + inProgressMinutes) / 60)),
    durationMinutes: toMinuteOption((devQaMinutes + inProgressMinutes) % 60),
  };
};

/**
 * Split a Dev QA Done total across selected cards by remaining board minutes
 * (largest-remainder), so each entry is validated against its own board actual.
 */
const allocateMinutesAcrossDevQaCards = (totalMinutes, cards = []) => {
  if (!cards.length || totalMinutes <= 0) return [];

  const weights = cards.map((card) => {
    const remaining = getCardRemainingBoardMinutes(card);
    if (remaining > 0) return remaining;
    const boardActual = Math.round(Number(card.actualHours || 0) * 60);
    return boardActual > 0 ? boardActual : 1;
  });
  const weightSum = weights.reduce((sum, weight) => sum + weight, 0);

  const raw = weights.map((weight) => (totalMinutes * weight) / weightSum);
  const floors = raw.map((value) => Math.floor(value));
  let remainder = totalMinutes - floors.reduce((sum, value) => sum + value, 0);
  const byFraction = raw
    .map((value, index) => ({ index, fraction: value - floors[index] }))
    .sort((a, b) => b.fraction - a.fraction);

  const minutes = [...floors];
  for (let i = 0; i < remainder; i += 1) {
    minutes[byFraction[i % byFraction.length].index] += 1;
  }

  return cards.map((card, index) => ({
    card,
    minutes: minutes[index],
  }));
};

const buildSubmitProjectsFromTasks = (tasks) => {
  const projects = [];

  tasks.forEach((task) => {
    if (usesBoardCardsDailyWork(task.selectedProject)) {
      const devQaCards = task.selectedDevQaCards || [];
      const inProgressCards = task.selectedInProgressCards || [];
      const devQaMinutes = getInternalDevQaMinutes(task);
      const overReason = String(task.devQaOverReason || "").trim();

      if (devQaCards.length && devQaMinutes > 0) {
        allocateMinutesAcrossDevQaCards(devQaMinutes, devQaCards).forEach(
          ({ card, minutes }) => {
            if (minutes <= 0) return;
            const remaining = getCardRemainingBoardMinutes(card);
            const needsOverReason = minutes > remaining;
            const reasonHtml = needsOverReason ? formatOverEstimateReasonHtml(overReason) : "";
            projects.push({
              projectId: task.selectedProject?.value,
              projectCategory: task.prjCategory?.value,
              duration: minutes,
              description:
                buildInternalTaskDescription([card], [], minutes, 0)
                + reasonHtml,
              status: "Completed",
              challenges: "",
              boardCardId: card.cardId,
              boardCardListType: "dev_qa_done",
              actualHoursOverReason: needsOverReason ? overReason : "",
            });
          }
        );
      }

      inProgressCards.forEach((card) => {
        const cardMinutes = getInProgressCardMinutes(card);
        if (cardMinutes <= 0) return;
        const overReasonText = String(card.inProgressOverReason || "").trim();
        const reasonHtml =
          inProgressCardNeedsOverReason(card) || overReasonText
            ? formatOverEstimateReasonHtml(overReasonText)
            : "";
        projects.push({
          projectId: task.selectedProject?.value,
          projectCategory: task.prjCategory?.value,
          duration: cardMinutes,
          description:
            `<p><strong>In-Progress:</strong> ${card.ticketNo ? `${escapeHtmlText(card.ticketNo)}: ` : ""}${escapeHtmlText(card.title)} — ${formatDurationHoursMinutes(cardMinutes)}</p>`
            + formatBoardCardDescriptionHtml(card)
            + formatBoardCardDoneChecklistHtml(card)
            + reasonHtml,
          status: "Completed",
          challenges: "",
          boardCardId: card.cardId,
          boardCardListType: "in_progress",
          actualHoursOverReason: overReasonText,
        });
      });
      return;
    }

    projects.push({
      projectId: task.selectedProject?.value,
      projectCategory: task.prjCategory?.value,
      duration: getTaskDurationMinutes(task),
      description: task.taskDescription,
      status: "Completed",
      challenges: "",
    });
  });

  return projects;
};

const isQuillEmpty = (html) => {
  const s = String(html ?? "").trim();
  if (!s) return true;
  if (s === "<p><br></p>" || s === "<p></p>") return true;
  return !s.replace(/<[^>]+>/g, "").trim();
};

const DailyExtrasField = ({ label, name, value, onChange, isReadOnly }) => (
  <div className="mb-4">
    <div className="form-label">{label}</div>
    {isReadOnly ? (
      <div className="daily-extras-readonly">
        {value?.trim() ? value : "—"}
      </div>
    ) : (
      <textarea
        className="daily-extras-textarea"
        name={name}
        value={value}
        onChange={onChange}
        placeholder={`Enter ${label.toLowerCase()}`}
        rows={3}
      />
    )}
  </div>
);

const isValidEmailList = (value) => {
  const raw = String(value ?? "").trim();
  if (!raw) return true;
  return splitDailyHoursNotifyEmails(raw).every((email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email));
};

const DailyExtrasQuillField = ({ label, value, onChange, isReadOnly, placeholder }) => (
  <div className="mb-4">
    <div className="form-label">{label}</div>
    {isReadOnly ? (
      isQuillEmpty(value) ? (
        <div className="daily-extras-readonly">—</div>
      ) : (
        <div
          className="task-description-readonly"
          dangerouslySetInnerHTML={{ __html: value }}
        />
      )
    ) : (
      <ReactQuill
        theme="snow"
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        readOnly={isReadOnly}
      />
    )}
  </div>
);

const ApplyHoursdrawer = ({
  toggleHoursDrawer,
  isOpen,
  selectedDate,
  isReadOnly = false,
  taskData = null,
  onTaskSubmitted,
  onMonthlyAccessBlocked,
}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [dailyWorkingHourLimit, setDailyWorkingHourLimit] = useState(0);
  const [projects, setProjects] = useState([]);
  const { workingHours } = useSelector((state) => state.task);
  const { user } = useSelector((state) => state.auth.auth);
  const [createLoading, setCreateLoading] = useState(false);
  const [tasks, setTasks] = useState([{ ...EMPTY_TASK }]);
  const [date, setDate] = useState("");
  const [loading, setLoading] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});
  const [dailyExtras, setDailyExtras] = useState({ ...EMPTY_DAILY_EXTRAS });
  const [showSendMailModal, setShowSendMailModal] = useState(false);
  const [sendingViewEmail, setSendingViewEmail] = useState(false);
  const [mailDraft, setMailDraft] = useState({ to: "", subject: "", body: "", cc: "", bcc: "" });
  const mailDraftRef = useRef({ to: "", subject: "", body: "", cc: "", bcc: "" });
  const [accessAlertOpen, setAccessAlertOpen] = useState(false);

  const canSendViewEmail = checkPermission(permissionType.DAILY_HOURS, "read users");
  const canSendDailyHoursMail =
    hasAdminAccess(user) || checkPermission(permissionType.DAILY_HOURS, "send mail");

  const adminName = useMemo(() => {
    const name = [user?.firstname, user?.lastname].filter(Boolean).join(" ").trim();
    return name || user?.email || "Admin";
  }, [user]);

  const viewEmployeeUserId = useMemo(
    () => taskData?.[0]?.userDetails?.userId ?? null,
    [taskData]
  );

  const viewEmployeeName = useMemo(() => {
    const details = taskData?.[0]?.userDetails;
    if (!details) return "Employee";
    const name = [details.firstname, details.lastname].filter(Boolean).join(" ").trim();
    return name || "Employee";
  }, [taskData]);

  const viewEmployeeNotifyEmail = useMemo(() => {
    const raw = taskData?.[0]?.userDetails?.dailyHoursNotifyEmail;
    const emails = splitDailyHoursNotifyEmails(raw);
    return emails.join(", ");
  }, [taskData]);

  const showSendMailButton =
    isReadOnly && canSendViewEmail && canSendDailyHoursMail && viewEmployeeUserId && date;

  useEffect(() => {
    mailDraftRef.current = mailDraft;
  }, [mailDraft]);

  const openSendMailDialog = useCallback(() => {
    setMailDraft({
      to: viewEmployeeNotifyEmail,
      subject: buildDailyHoursViewMailSubject({ date }),
      body: buildDailyHoursViewMailPlainText({ tasks, dailyExtras, adminName }),
      cc: "",
      bcc: "",
    });
    setShowSendMailModal(true);
  }, [date, tasks, dailyExtras, adminName, viewEmployeeNotifyEmail]);

  const resetSendMailDialog = useCallback(() => {
    setShowSendMailModal(false);
    setMailDraft({ to: "", subject: "", body: "", cc: "", bcc: "" });
  }, []);

  const handleSendViewEmail = useCallback(async () => {
    if (!viewEmployeeUserId || !date || !tasks.length || sendingViewEmail) return;
    if (!canSendDailyHoursMail) {
      showErrorToast("You do not have permission to send daily work updates emails.");
      return;
    }
    const draft = mailDraftRef.current;
    if (!splitDailyHoursNotifyEmails(draft.to).length) {
      showErrorToast("At least one recipient email is required in To.");
      return;
    }
    if (!isValidEmailList(draft.to)) {
      showErrorToast("Enter valid To emails separated by commas.");
      return;
    }
    if (!draft.subject.trim() || !draft.body.trim()) {
      showErrorToast("Email subject and message are required.");
      return;
    }
    if (!isValidEmailList(draft.cc)) {
      showErrorToast("Enter valid CC emails separated by commas.");
      return;
    }
    if (!isValidEmailList(draft.bcc)) {
      showErrorToast("Enter valid BCC emails separated by commas.");
      return;
    }

    setSendingViewEmail(true);
    try {
      await sendDailyHoursViewEmail({
        userId: viewEmployeeUserId,
        date,
        to: draft.to,
        subject: draft.subject,
        body: draft.body,
        emailSubject: draft.subject,
        emailBody: draft.body,
        cc: draft.cc,
        bcc: draft.bcc,
        senderName: adminName,
        sentBy: adminName,
      });
      showSuccessToast("Daily work updates email sent successfully.");
      resetSendMailDialog();
    } catch (err) {
      const message =
        err?.response?.data?.error?.message ||
        err?.response?.data?.error ||
        err?.message ||
        "Unable to send daily work updates email.";
      showErrorToast(typeof message === "string" ? message : "Unable to send daily work updates email.");
    } finally {
      setSendingViewEmail(false);
    }
  }, [
    viewEmployeeUserId,
    date,
    tasks.length,
    sendingViewEmail,
    canSendDailyHoursMail,
    adminName,
    resetSendMailDialog,
  ]);

  useEffect(() => {
    if (!isOpen) {
      setShowSendMailModal(false);
      return;
    }
    if (isOpen) {
      setLoading(true);
      if (!isReadOnly) fetchProjects();

      if (selectedDate) {
        const formattedDate = moment(selectedDate).format("YYYY-MM-DD");
        setDate(formattedDate);
      }

      if (isReadOnly && taskData) {
        const formattedTasks = [];

        taskData?.[0]?.taskDetails?.forEach((task) => {
          formattedTasks.push({
            selectedProject: {
              value: task.projectId,
              label: task.projectName,
            },
            prjCategory: {
              value: task.projectCategory,
              label: task.projectCategory,
            },
            ...minutesToDurationParts(task.duration),
            taskDescription: task.description,
            status: task.status,
            challenges: task.challenges,
          });
        });

        setTasks(formattedTasks);

        const extras = taskData?.[0]?.dailyExtras;
        setDailyExtras(
          extras
            ? { ...EMPTY_DAILY_EXTRAS, ...extras }
            : { ...EMPTY_DAILY_EXTRAS }
        );
      } else if (!isReadOnly) {
        setDailyExtras({ ...EMPTY_DAILY_EXTRAS });
      }
    }
  }, [isOpen, selectedDate, isReadOnly, taskData]);

  useEffect(() => {
    if (!isOpen || !selectedDate) return;
    dispatch(getWorkingHourAPI(moment(selectedDate).format("YYYY-MM-DD")));
  }, [dispatch, isOpen, selectedDate]);

  const leaveInfo = useMemo(() => {
    const data = workingHours.data;
    if (!data || typeof data !== "object") return null;
    return data.leave ?? null;
  }, [workingHours.data]);

  useEffect(() => {
    const data = workingHours.data;
    if (data && typeof data === "object" && "workingHours" in data) {
      const leave = data.leave ?? null;
      setDailyWorkingHourLimit(
        leave
          ? getRequiredWorkingHours({
            ...leave,
            workingHours:
              typeof data.workingHours === "number"
                ? data.workingHours
                : undefined,
          })
          : typeof data.workingHours === "number"
            ? data.workingHours
            : 8
      );
      return;
    }
    if (typeof data === "number") {
      setDailyWorkingHourLimit(data);
      return;
    }
    setDailyWorkingHourLimit(8);
  }, [workingHours.data]);
  const fetchProjects = async () => {
    try {
      const response = await dispatch(getAllProject());
      if (response.payload && response.payload.data) {
        const projectOptions = response.payload.data.map((project) => ({
          value: project.projectId,
          label: project.projectName,
          projectType: project.projectType || "client",
          isBoardMember: Boolean(project.isBoardMember),
        }));
        setProjects(projectOptions);
      }
    } catch (error) {
      console.error("Error fetching projects:", error);
    } finally {
      setLoading(false);
    }
  };

  const loadBoardCardsForTask = useCallback(async (index, projectOption) => {
    if (!projectOption || !usesBoardCardsDailyWork(projectOption) || !date) return;

    setTasks((current) => {
      const next = [...current];
      if (!next[index]) return current;
      next[index] = {
        ...next[index],
        boardCardsLoading: true,
        boardCardOptions: [],
        selectedDevQaCards: [],
        selectedInProgressCards: [],
        devQaDurationHours: null,
        devQaDurationMinutes: null,
        devQaOverReason: "",
        taskDescription: "",
        durationHours: null,
        durationMinutes: null,
      };
      return next;
    });

    try {
      const response = await getDailyWorkBoardCardsAPI({
        date,
        type: "daily_work",
        projectId: projectOption.value,
      });
      const cards = (response || []).map(formatBoardCardOption);
      setTasks((current) => {
        const next = [...current];
        if (!next[index] || next[index].selectedProject?.value !== projectOption.value) {
          return current;
        }
        next[index] = {
          ...next[index],
          boardCardOptions: cards,
          boardCardsLoading: false,
        };
        return next;
      });
    } catch (error) {
      console.error("Error loading board cards:", error);
      setTasks((current) => {
        const next = [...current];
        if (next[index]) next[index].boardCardsLoading = false;
        return next;
      });
      showErrorToast("Unable to load board tasks from the project board.");
    }
  }, [date]);

  const handleProjectSelect = (index, projectOption) => {
    const updatedTasks = [...tasks];
    updatedTasks[index] = {
      ...updatedTasks[index],
      selectedProject: projectOption,
      selectedDevQaCards: [],
      selectedInProgressCards: [],
      devQaDurationHours: null,
      devQaDurationMinutes: null,
      devQaOverReason: "",
      taskDescription: "",
      durationHours: null,
      durationMinutes: null,
      boardCardsLoading: Boolean(projectOption && usesBoardCardsDailyWork(projectOption)),
    };
    setTasks(updatedTasks);

    if (validationErrors[`project_${index}`]) {
      const newValidationErrors = { ...validationErrors };
      delete newValidationErrors[`project_${index}`];
      setValidationErrors(newValidationErrors);
    }

    if (projectOption && usesBoardCardsDailyWork(projectOption)) {
      loadBoardCardsForTask(index, projectOption);
    }
  };

  const handleDevQaCardsSelect = (index, selectedOptions, availableCards) => {
    const selected = (selectedOptions || []).map((option) => {
      const match = (availableCards || []).find((card) => card.cardId === option.value);
      return match ? { ...match } : { ...option, cardId: option.value };
    });

    setTasks((current) => {
      const next = [...current];
      const task = next[index];
      if (!task) return current;
      next[index] = syncInternalTaskState({
        ...task,
        selectedDevQaCards: selected,
      });
      return next;
    });
    clearInternalValidation(index);
  };

  const handleInProgressCardsSelect = (index, selectedOptions, availableCards) => {
    setTasks((current) => {
      const next = [...current];
      const task = next[index];
      if (!task) return current;

      const selected = (selectedOptions || []).map((option) => {
        const match = (availableCards || []).find((card) => card.cardId === option.value);
        const existing = (task.selectedInProgressCards || []).find(
          (card) => card.cardId === option.value
        );
        const base = match ? { ...match } : { ...option, cardId: option.value };
        return {
          ...base,
          inProgressHours: existing?.inProgressHours ?? null,
          inProgressMinutes: existing?.inProgressMinutes ?? null,
          inProgressOverReason: existing?.inProgressOverReason ?? "",
        };
      });

      next[index] = syncInternalTaskState({
        ...task,
        selectedInProgressCards: selected,
      });
      return next;
    });
    clearInternalValidation(index);
  };

  const updateDevQaSectionOverReason = (index, value) => {
    setTasks((current) => {
      const next = [...current];
      const task = next[index];
      if (!task) return current;
      next[index] = syncInternalTaskState({ ...task, devQaOverReason: value });
      return next;
    });
  };

  const updateDevQaDuration = (index, part, option) => {
    setTasks((current) => {
      const next = [...current];
      const task = next[index];
      if (!task) return current;

      const updated = { ...task, [part]: option };
      if (part === "devQaDurationHours") {
        const hours = parseInt(option?.value || "0", 10);
        const allowedMinutes = getMinuteOptionsForHours(hours);
        const currentMinute = updated.devQaDurationMinutes?.value;
        if (currentMinute && !allowedMinutes.some((o) => o.value === currentMinute)) {
          updated.devQaDurationMinutes = null;
        }
      }

      next[index] = syncInternalTaskState(updated);
      return next;
    });
    clearInternalValidation(index);
  };

  const updateInProgressCardOverReason = (index, cardId, value) => {
    setTasks((current) => {
      const next = [...current];
      const task = next[index];
      if (!task) return current;

      const selectedInProgressCards = (task.selectedInProgressCards || []).map((card) =>
        card.cardId === cardId ? { ...card, inProgressOverReason: value } : card
      );

      next[index] = syncInternalTaskState({ ...task, selectedInProgressCards });
      return next;
    });
  };

  const updateInProgressCardDuration = (index, cardId, part, option) => {
    setTasks((current) => {
      const next = [...current];
      const task = next[index];
      if (!task) return current;

      const selectedInProgressCards = (task.selectedInProgressCards || []).map((card) => {
        if (card.cardId !== cardId) return card;

        const updated = { ...card, [part]: option };
        if (part === "inProgressHours") {
          const hours = parseInt(option?.value || "0", 10);
          const allowedMinutes = getMinuteOptionsForHours(hours);
          const currentMinute = updated.inProgressMinutes?.value;
          if (currentMinute && !allowedMinutes.some((o) => o.value === currentMinute)) {
            updated.inProgressMinutes = null;
          }
        }
        return updated;
      });

      next[index] = syncInternalTaskState({
        ...task,
        selectedInProgressCards,
      });
      return next;
    });
    clearInternalValidation(index);
  };

  const clearInternalValidation = (index) => {
    setValidationErrors((current) => {
      const next = { ...current };
      delete next[`description_${index}`];
      delete next[`duration_${index}`];
      Object.keys(next).forEach((key) => {
        if (
          key.startsWith(`dev_qa_reason_${index}`)
          || key.startsWith(`dev_qa_duration_${index}`)
          || key.startsWith(`in_progress_duration_${index}`)
          || key.startsWith(`in_progress_reason_${index}`)
        ) {
          delete next[key];
        }
      });
      return next;
    });
  };

  const calculateTotalMinutes = () => {
    return tasks.reduce(
      (total, task) => total + getTaskDurationMinutes(task),
      0
    );
  };

  const isCommpletedDailyWork = () => {
    return calculateTotalMinutes() >= dailyWorkingHourLimit * 60;
  };

  const partialLeaveStatusLabel = useMemo(() => {
    if (!leaveInfo || !isPartialLeaveDay(leaveInfo) || !date) return null;
    const isCompliant = calculateTotalMinutes() >= dailyWorkingHourLimit * 60;
    const complianceStatus = getPartialLeaveComplianceStatus({
      isCompliant,
      isPending: isDailyWorkUpdatePending(date),
    });
    return getPartialLeaveStatusLabel(complianceStatus);
  }, [leaveInfo, date, tasks, dailyWorkingHourLimit]);

  const validateTask = (task, index) => {
    let errors = {};

    if (!task.selectedProject) {
      errors[`project_${index}`] = true;
    }

    if (!task.prjCategory) {
      errors[`category_${index}`] = true;
    }

    if (
      !usesBoardCardsDailyWork(task.selectedProject)
      && getTaskDurationMinutes(task) <= 0
    ) {
      errors[`duration_${index}`] = true;
    }

    if (
      usesBoardCardsDailyWork(task.selectedProject)
        ? !(task.selectedDevQaCards?.length || task.selectedInProgressCards?.length)
        : (
          !task.taskDescription ||
          task.taskDescription.trim() === "" ||
          task.taskDescription === "<p><br></p>"
        )
    ) {
      errors[`description_${index}`] = true;
    }

    if (usesBoardCardsDailyWork(task.selectedProject)) {
      const hasDevQa = (task.selectedDevQaCards || []).length > 0;

      if (hasDevQa && getInternalDevQaMinutes(task) <= 0) {
        errors[`dev_qa_duration_${index}`] = true;
      }
      (task.selectedInProgressCards || []).forEach((card) => {
        if (getInProgressCardMinutes(card) <= 0) {
          errors[`in_progress_duration_${index}_${card.cardId}`] = true;
        }
        if (
          inProgressCardNeedsOverReason(card)
          && !String(card.inProgressOverReason || "").trim()
        ) {
          errors[`in_progress_reason_${index}_${card.cardId}`] = true;
        }
      });
      if (devQaDurationNeedsOverReason(task) && !String(task.devQaOverReason || "").trim()) {
        errors[`dev_qa_reason_${index}`] = true;
      }
    }

    return errors;
  };

  const addNewTask = () => {
    // Validate the last task before adding a new one
    const lastTaskIndex = tasks.length - 1; // Last task in the array
    const lastTask = tasks[lastTaskIndex];

    const errors = validateTask(lastTask, lastTaskIndex);

    if (Object.keys(errors).length > 0) {
      setValidationErrors({ ...validationErrors, ...errors });
      showErrorToast(
        "Please fill all fields of the previous task before adding a new one."
      );
      return;
    }

    // If validation passes, add a new task at the beginning of the array
    setTasks([
      ...tasks,
      { ...EMPTY_TASK },
    ]);

    // Clear validation errors
    setValidationErrors({});
  };

  const removeTask = (index) => {
    if (tasks.length > 1) {
      const updatedTasks = [...tasks];
      updatedTasks.splice(index, 1);
      setTasks(updatedTasks);

      // Clear validation errors for the removed task
      const newValidationErrors = { ...validationErrors };
      delete newValidationErrors[`project_${index}`];
      delete newValidationErrors[`category_${index}`];
      delete newValidationErrors[`duration_${index}`];
      delete newValidationErrors[`description_${index}`];
      setValidationErrors(newValidationErrors);
    }
  };

  const updateDailyExtra = (field, value) => {
    setDailyExtras((prev) => ({ ...prev, [field]: value }));
  };

  const updateTask = (index, field, value) => {
    const updatedTasks = [...tasks];
    updatedTasks[index][field] = value;
    setTasks(updatedTasks);

    // Clear validation error for this field if it exists
    if (field === "selectedProject" && validationErrors[`project_${index}`]) {
      const newValidationErrors = { ...validationErrors };
      delete newValidationErrors[`project_${index}`];
      setValidationErrors(newValidationErrors);
    } else if (
      field === "prjCategory" &&
      validationErrors[`category_${index}`]
    ) {
      const newValidationErrors = { ...validationErrors };
      delete newValidationErrors[`category_${index}`];
      setValidationErrors(newValidationErrors);
    } else if (
      (field === "durationHours" || field === "durationMinutes") &&
      validationErrors[`duration_${index}`]
    ) {
      const newValidationErrors = { ...validationErrors };
      delete newValidationErrors[`duration_${index}`];
      setValidationErrors(newValidationErrors);
    } else if (
      field === "taskDescription" &&
      validationErrors[`description_${index}`]
    ) {
      const newValidationErrors = { ...validationErrors };
      delete newValidationErrors[`description_${index}`];
      setValidationErrors(newValidationErrors);
    }
  };

  const updateTaskDuration = (index, part, option) => {
    const task = tasks[index];
    if (usesBoardCardsDailyWork(task?.selectedProject)) return;

    const updatedTasks = [...tasks];
    updatedTasks[index][part] = option;

    const hours = parseInt(updatedTasks[index].durationHours?.value || "0", 10);
    const allowedMinutes = getMinuteOptionsForHours(hours);
    const currentMinuteValue = updatedTasks[index].durationMinutes?.value;

    if (
      currentMinuteValue &&
      !allowedMinutes.some((o) => o.value === currentMinuteValue)
    ) {
      updatedTasks[index].durationMinutes = null;
    }

    const taskAfterDuration = updatedTasks[index];
    setTasks(updatedTasks);

    if (validationErrors[`duration_${index}`]) {
      const newValidationErrors = { ...validationErrors };
      delete newValidationErrors[`duration_${index}`];
      setValidationErrors(newValidationErrors);
    }
  };

  const handleSubmit = async () => {
    if (isFullLeaveDay(leaveInfo)) {
      showErrorToast("No work update is required on a full leave day.");
      return;
    }
    if (!date || !isCommpletedDailyWork()) {
      showErrorToast(
        leaveInfo
          ? `Please log the remaining required hours (${formatDurationHoursMinutes(dailyWorkingHourLimit * 60)}).`
          : "Please complete the daily work."
      );
      return;
    }

    // Validate all tasks
    let allErrors = {};
    let hasErrors = false;

    tasks.forEach((task, index) => {
      const taskErrors = validateTask(task, index);
      if (Object.keys(taskErrors).length > 0) {
        allErrors = { ...allErrors, ...taskErrors };
        hasErrors = true;
      }
    });

    if (hasErrors) {
      setValidationErrors(allErrors);
      showErrorToast("Please fill all required fields.");
      return;
    }

    try {
      const submitProjects = buildSubmitProjectsFromTasks(tasks);
      const payload = {
        date: moment(date).format("YYYY-MM-DD"),
        projects: submitProjects,
        ...dailyExtras,
      };
      setCreateLoading(true);
      dispatch(createTaskAPI(payload))
        .unwrap()
        .then(() => {
          const updatedTask = [
            {
              userDetails: {
                userId: user.id,
              },
              taskDetails: submitProjects.map((project) => {
                const sourceTask = tasks.find(
                  (task) => task.selectedProject?.value === project.projectId
                );
                return {
                  projectId: project.projectId,
                  projectName: sourceTask?.selectedProject?.label,
                  projectCategory: project.projectCategory,
                  duration: project.duration,
                  description: project.description,
                  status: project.status,
                  challenges: "",
                };
              }),
              dailyExtras,
            },
          ];
          dispatch(
            addTask({
              date: moment(date).format("YYYY-MM-DD"),
              payload: updatedTask,
            })
          );
          if (typeof onTaskSubmitted === "function") {
            onTaskSubmitted();
          }
          setCreateLoading(false);
          resetForm();
          toggleHoursDrawer(); // Close the drawer on successful submission
        })
        .catch((err) => {
          setCreateLoading(false);
          if (isMonthlyAccessBlockedError(err)) {
            if (typeof onMonthlyAccessBlocked === "function") {
              onMonthlyAccessBlocked();
            } else {
              if (isOpen) toggleHoursDrawer();
              setAccessAlertOpen(true);
            }
            return;
          }
          const message =
            err?.message || err?.error || "Unable to submit daily work updates.";
          showErrorToast(
            typeof message === "string"
              ? message
              : "Unable to submit daily work updates."
          );
        });
    } catch (error) {
      console.error("Error submitting daily work: ", error);
    }
  };

  const resetForm = () => {
    setTasks([{ ...EMPTY_TASK }]);
    setDate(selectedDate ? moment(selectedDate).format("YYYY-MM-DD") : "");
    setDailyExtras({ ...EMPTY_DAILY_EXTRAS });
  };

  const customStyles = {
    control: (base) => ({
      ...base,
      minHeight: "44px",
      borderRadius: "0.375rem",
      border: "1.5px solid #e5e7eb",
      boxShadow: "none",
      backgroundColor: "#ffffff",
      "&:hover": {
        border: "1.5px solid #0c4a6e",
      },
    }),
    menuPortal: (base) => ({ ...base, zIndex: 4000 }),
    menu: (base) => ({ ...base, backgroundColor: "#ffffff", zIndex: 4000 }),
    menuList: (base) => ({ ...base, backgroundColor: "#ffffff" }),
    option: (base, state) => ({
      ...base,
      backgroundColor: state.isSelected ? "#6366f1" : state.isFocused ? "#eef2ff" : "#ffffff",
      color: state.isSelected ? "#ffffff" : "#111827",
    }),
    singleValue: (base) => ({ ...base, color: "#111827" }),
    multiValue: (base) => ({
      ...base,
      backgroundColor: "#eef2ff",
      borderRadius: "0.25rem",
    }),
    multiValueLabel: (base) => ({
      ...base,
      color: "#4338ca",
      fontSize: "0.8125rem",
    }),
    multiValueRemove: (base) => ({
      ...base,
      color: "#6366f1",
      ":hover": {
        backgroundColor: "#c7d2fe",
        color: "#312e81",
      },
    }),
    placeholder: (base) => ({
      ...base,
      color: "#9CA3AF",
    }),
  };

  return (
    <div>
      <MonthlyAccessRequestModal
        open={accessAlertOpen}
        onClose={() => setAccessAlertOpen(false)}
        onGoToPreviousMonth={() => {
          setAccessAlertOpen(false);
          navigate("/daily-hours");
        }}
      />
      <Drawer
        open={isOpen}
        onClose={toggleHoursDrawer}
        placement={window.innerWidth > 700 ? "right" : "bottom"}
        className="drawer"
        title={isReadOnly ? "View Daily Work Updates" : "Add Daily Work Updates"}
        width="600px"
      >
        {workingHours.loading ? (
          <div className="h-full w-full flex item-center justify-center ">
            <Loader />
          </div>
        ) : (
          <div className="drawer-container">
            {showSendMailButton ? (
              <div className="daily-hours-send-mail ">
                <button
                  type="button"
                  className="daily-hours-send-mail__btn"
                  onClick={openSendMailDialog}
                >
                  Send mail
                </button>
                <p className="daily-hours-send-mail__hint">
                  Sends a table-formatted update (same layout as submission emails)
                </p>
              </div>
            ) : null}
            <Input
              placeholder="Select Date"
              label="Date"
              type="date"
              name="date"
              id="date"
              disabled={true}
              value={date}
              className="w-[100%] mb-4"
              classNameInput="leave-date"
              onChange={(e) => { }}
            />

            {leaveInfo && (
              <div
                className={`daily-work-leave-banner daily-work-leave-banner--${leaveInfo.kind || "other"}`}
                role="status"
              >
                <p className="daily-work-leave-banner__message">
                  {getLeaveBannerMessage(leaveInfo)}
                </p>
                {partialLeaveStatusLabel && (
                  <div className="daily-work-leave-banner__status-stack">
                    <span className="daily-work-leave-banner__status-leave">
                      {leaveInfo?.label}
                    </span>
                    <span className="daily-work-leave-banner__status-value">
                      {partialLeaveStatusLabel}
                    </span>
                    {!isReadOnly && (
                      <span className="daily-work-leave-banner__logged">
                        {formatDurationHoursMinutes(calculateTotalMinutes())}{" "}
                        /{" "}
                        {formatDurationHoursMinutes(
                          dailyWorkingHourLimit * 60
                        )}{" "}
                        logged
                      </span>
                    )}
                  </div>
                )}
              </div>
            )}

            {!isFullLeaveDay(leaveInfo) && (
              <div className=" flex justify-between items-center">
                <div className="text-md font-medium">
                  Total: {formatDurationHoursMinutes(calculateTotalMinutes())} /{" "}
                  {formatDurationHoursMinutes(dailyWorkingHourLimit * 60)}
                </div>
                {!isReadOnly && (
                  <div className="flex justify-end">
                    <button
                      className="px-4 py-2  text-white rounded-md bg-sky-900 cursor-pointer"
                      onClick={addNewTask}
                    >
                      Add Task
                    </button>
                  </div>
                )}
              </div>
            )}

            {!isFullLeaveDay(leaveInfo) && (
              <>
                <div className="flex flex-col-reverse">
                  {tasks.map((task, index) => {
                    const boardSplit = splitBoardCardOptions(task.boardCardOptions || []);
                    return (
                      <div key={index} className="mb-8 border p-4 rounded-md min-w-0">
                        <div className="flex justify-between mb-2">
                          <h3 className="text-lg font-medium">
                            Project {index + 1}
                          </h3>
                          {!isReadOnly && tasks.length > 1 && (
                            <button
                              className="text-red-500"
                              onClick={() => removeTask(index)}
                            >
                              Remove
                            </button>
                          )}
                        </div>

                        <div className="mb-4">
                          <div className="form-label">Project</div>
                          <Select
                            value={task.selectedProject}
                            onChange={(value) => handleProjectSelect(index, value)}
                            options={projects}
                            placeholder="Select Project"
                            isLoading={loading}
                            isClearable={!isReadOnly}
                            styles={customStyles}
                            className="react-select-container"
                            classNamePrefix="react-select"
                            menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                            menuPosition="fixed"
                            isDisabled={isReadOnly}
                          />
                          <ErrorText
                            message={"Please select a project."}
                            show={validationErrors[`project_${index}`]}
                          />
                          {usesBoardCardsDailyWork(task.selectedProject) ? (
                            <p className="text-xs text-[#6b7280] mt-1">
                              Internal project — select Dev QA Done tasks first, then In-Progress tasks below.
                            </p>
                          ) : null}
                        </div>

                        <SelectInput
                          label="Project Category"
                          selected={task.prjCategory}
                          placeholder="Select Project Category"
                          type="projectCategory"
                          manualOptions={[]}
                          className="w-[100%] mb-4"
                          classNameInput="leave-date"
                          onChange={(option) =>
                            updateTask(index, "prjCategory", option)
                          }
                          isMulti={false}
                          isDisabled={isReadOnly}
                          isClearable={!isReadOnly}
                        />
                        <ErrorText
                          message={"Please select a project category."}
                          show={validationErrors[`category_${index}`]}
                        />

                        {usesBoardCardsDailyWork(task.selectedProject) ? (
                          <div className="mb-4 internal-board-tasks">
                            {isReadOnly ? (
                              <div
                                className="task-description-readonly"
                                dangerouslySetInnerHTML={{
                                  __html: task.taskDescription,
                                }}
                              />
                            ) : task.boardCardsLoading ? (
                              <div className="internal-board-tasks__loading">
                                <Loader />
                              </div>
                            ) : (
                              <>
                                <section className="internal-board-tasks__section">
                                  <h4 className="internal-board-tasks__title">Dev QA Done</h4>
                                  <p className="internal-board-tasks__hint">
                                    Select tasks you moved to Dev QA Done on this date, then enter total hours spent.
                                  </p>
                                  <div className="form-label text-xs">Board Task (Dev QA Done)</div>
                                  <Select
                                    value={cardsToSelectValue(task.selectedDevQaCards)}
                                    onChange={(value) =>
                                      handleDevQaCardsSelect(index, value, boardSplit.devQaCards)
                                    }
                                    options={boardSplit.devQaCards.map(toBoardTaskSelectOption)}
                                    formatOptionLabel={(option) => (
                                      <div className="internal-board-tasks__option">
                                        <span>{option.label}</span>
                                        <span className="internal-board-tasks__option-hours">
                                          {formatCardHoursLabel(option.actualHours || 0)}
                                          {option.previouslyLoggedMinutes > 0
                                            ? ` · logged ${formatDurationHoursMinutes(option.previouslyLoggedMinutes)}`
                                            : ""}
                                        </span>
                                      </div>
                                    )}
                                    placeholder={
                                      boardSplit.devQaCards.length
                                        ? "Select Dev QA Done tasks"
                                        : "No Dev QA Done tasks moved on today"
                                    }
                                    isMulti
                                    isSearchable
                                    isClearable
                                    closeMenuOnSelect={false}
                                    styles={customStyles}
                                    className="react-select-container internal-board-tasks__select"
                                    classNamePrefix="react-select"
                                    menuPortalTarget={
                                      typeof document !== "undefined" ? document.body : null
                                    }
                                    menuPosition="fixed"
                                    isDisabled={isReadOnly || !boardSplit.devQaCards.length}
                                    noOptionsMessage={() => "No Dev QA Done tasks moved on today"}
                                  />
                                  {task.selectedDevQaCards?.length ? (
                                    <>
                                      <p className="internal-board-tasks__board-total">
                                        Board actual total:{" "}
                                        {formatDurationHoursMinutes(
                                          getBoardActualMinutesForSelectedDevQa(task)
                                        )}
                                        {getPreviouslyLoggedMinutesForSelectedDevQa(task) > 0 ? (
                                          <>
                                            {" "}
                                            · Previously logged:{" "}
                                            {formatDurationHoursMinutes(
                                              getPreviouslyLoggedMinutesForSelectedDevQa(task)
                                            )}
                                            {" "}
                                            · Remaining:{" "}
                                            {formatDurationHoursMinutes(
                                              getRemainingBoardMinutesForSelectedDevQa(task)
                                            )}
                                          </>
                                        ) : null}
                                      </p>
                                      <div className="internal-board-tasks__duration">
                                        <div className="duration-select-row grid grid-cols-2 gap-3">
                                          <div>
                                            <div className="form-label text-xs">Hours</div>
                                            <Select
                                              value={task.devQaDurationHours}
                                              onChange={(value) =>
                                                updateDevQaDuration(index, "devQaDurationHours", value)
                                              }
                                              options={hourOptions}
                                              placeholder="Hours"
                                              styles={customStyles}
                                              className="react-select-container"
                                              classNamePrefix="react-select"
                                              menuPortalTarget={
                                                typeof document !== "undefined" ? document.body : null
                                              }
                                              menuPosition="fixed"
                                              isDisabled={isReadOnly}
                                            />
                                          </div>
                                          <div>
                                            <div className="form-label text-xs">Minutes</div>
                                            <Select
                                              value={task.devQaDurationMinutes}
                                              onChange={(value) =>
                                                updateDevQaDuration(index, "devQaDurationMinutes", value)
                                              }
                                              options={getMinuteOptionsForHours(
                                                parseInt(task.devQaDurationHours?.value || "0", 10)
                                              )}
                                              placeholder="Minutes"
                                              styles={customStyles}
                                              className="react-select-container"
                                              classNamePrefix="react-select"
                                              menuPortalTarget={
                                                typeof document !== "undefined" ? document.body : null
                                              }
                                              menuPosition="fixed"
                                              isDisabled={isReadOnly}
                                            />
                                          </div>
                                        </div>
                                      </div>
                                      {devQaDurationNeedsOverReason(task) ? (
                                        <div className="internal-board-tasks__over-reason">
                                          <div className="form-label text-xs">
                                            Why did this take longer than remaining board time? (
                                            {formatDurationHoursMinutes(
                                              getRemainingBoardMinutesForSelectedDevQa(task)
                                            )}{" "}
                                            remaining)
                                          </div>
                                          <textarea
                                            className="internal-board-tasks__reason"
                                            rows={2}
                                            placeholder="Explain why actual hours exceed board time"
                                            value={task.devQaOverReason || ""}
                                            onChange={(e) =>
                                              updateDevQaSectionOverReason(index, e.target.value)
                                            }
                                            disabled={isReadOnly}
                                          />
                                        </div>
                                      ) : null}
                                      <ErrorText
                                        message={"Please select hours and/or minutes for Dev QA Done."}
                                        show={validationErrors[`dev_qa_duration_${index}`]}
                                      />
                                      <ErrorText
                                        message={"Please explain why Dev QA Done took longer than board actual time."}
                                        show={validationErrors[`dev_qa_reason_${index}`]}
                                      />
                                    </>
                                  ) : null}
                                </section>

                                <section className="internal-board-tasks__section">
                                  <h4 className="internal-board-tasks__title">In-Progress</h4>
                                  <p className="internal-board-tasks__hint">
                                    Select tasks that were In-Progress on this date and enter hours spent on each.
                                  </p>
                                  <div className="form-label text-xs">Board Task (In-Progress)</div>
                                  <Select
                                    value={cardsToSelectValue(task.selectedInProgressCards)}
                                    onChange={(value) =>
                                      handleInProgressCardsSelect(index, value, boardSplit.inProgressCards)
                                    }
                                    options={boardSplit.inProgressCards.map(toBoardTaskSelectOption)}
                                    placeholder={
                                      boardSplit.inProgressCards.length
                                        ? "Select In-Progress tasks"
                                        : "No In-Progress tasks for this date"
                                    }
                                    isMulti
                                    isSearchable
                                    isClearable
                                    closeMenuOnSelect={false}
                                    styles={customStyles}
                                    className="react-select-container internal-board-tasks__select"
                                    classNamePrefix="react-select"
                                    menuPortalTarget={
                                      typeof document !== "undefined" ? document.body : null
                                    }
                                    menuPosition="fixed"
                                    isDisabled={isReadOnly || !boardSplit.inProgressCards.length}
                                    noOptionsMessage={() => "No In-Progress tasks for this date"}
                                  />
                                  {task.selectedInProgressCards?.length ? (
                                    <div className="internal-board-tasks__card-durations">
                                      {task.selectedInProgressCards.map((card) => (
                                        <div
                                          key={card.cardId}
                                          className="internal-board-tasks__card-duration"
                                        >
                                          <div className="form-label text-xs">
                                            {getBoardTaskDisplayLabel(card)}
                                            {card.estimatedHours != null && card.estimatedHours !== "" ? (
                                              <span className="internal-board-tasks__card-prior">
                                                {" "}
                                                (estimated {formatCardHoursLabel(card.estimatedHours)})
                                              </span>
                                            ) : null}
                                            {getCardPreviouslyLoggedMinutes(card) > 0 ? (
                                              <span className="internal-board-tasks__card-prior">
                                                {" "}
                                                · logged before{" "}
                                                {formatDurationHoursMinutes(
                                                  getCardPreviouslyLoggedMinutes(card)
                                                )}
                                              </span>
                                            ) : null}
                                            {/* {getInProgressCardRemainingEstimatedMinutes(card) != null ? (
                                              <span className="internal-board-tasks__card-prior">
                                                {" "}
                                                · remaining estimate{" "}
                                                {formatDurationHoursMinutes(
                                                  getInProgressCardRemainingEstimatedMinutes(card)
                                                )}
                                              </span>
                                            ) : null} */}
                                          </div>
                                          <div className="duration-select-row grid grid-cols-2 gap-3">
                                            <div>
                                              <div className="form-label text-xs">Hours</div>
                                              <Select
                                                value={card.inProgressHours}
                                                onChange={(value) =>
                                                  updateInProgressCardDuration(
                                                    index,
                                                    card.cardId,
                                                    "inProgressHours",
                                                    value
                                                  )
                                                }
                                                options={hourOptions}
                                                placeholder="Hours"
                                                styles={customStyles}
                                                className="react-select-container"
                                                classNamePrefix="react-select"
                                                menuPortalTarget={
                                                  typeof document !== "undefined" ? document.body : null
                                                }
                                                menuPosition="fixed"
                                                isDisabled={isReadOnly}
                                              />
                                            </div>
                                            <div>
                                              <div className="form-label text-xs">Minutes</div>
                                              <Select
                                                value={card.inProgressMinutes}
                                                onChange={(value) =>
                                                  updateInProgressCardDuration(
                                                    index,
                                                    card.cardId,
                                                    "inProgressMinutes",
                                                    value
                                                  )
                                                }
                                                options={getMinuteOptionsForHours(
                                                  parseInt(card.inProgressHours?.value || "0", 10)
                                                )}
                                                placeholder="Minutes"
                                                styles={customStyles}
                                                className="react-select-container"
                                                classNamePrefix="react-select"
                                                menuPortalTarget={
                                                  typeof document !== "undefined" ? document.body : null
                                                }
                                                menuPosition="fixed"
                                                isDisabled={isReadOnly}
                                              />
                                            </div>
                                          </div>
                                          {inProgressCardNeedsOverReason(card) ? (
                                            <div className="internal-board-tasks__over-reason">
                                              <div className="form-label text-xs">
                                                Why did this take longer than estimated? (
                                                {formatCardHoursLabel(card.estimatedHours)}
                                                {" "}
                                                estimated,
                                                {" "}
                                                {formatDurationHoursMinutes(
                                                  getCardPreviouslyLoggedMinutes(card)
                                                  + getInProgressCardMinutes(card)
                                                )}{" "}
                                                total logged)
                                              </div>
                                              <textarea
                                                className="internal-board-tasks__reason"
                                                rows={2}
                                                placeholder="Explain why total hours exceed the estimate"
                                                value={card.inProgressOverReason || ""}
                                                onChange={(e) =>
                                                  updateInProgressCardOverReason(
                                                    index,
                                                    card.cardId,
                                                    e.target.value
                                                  )
                                                }
                                                disabled={isReadOnly}
                                              />
                                            </div>
                                          ) : null}
                                          <ErrorText
                                            message={"Please select hours and/or minutes for this task."}
                                            show={
                                              validationErrors[
                                              `in_progress_duration_${index}_${card.cardId}`
                                              ]
                                            }
                                          />
                                          <ErrorText
                                            message={"Please explain why total hours exceed the estimate."}
                                            show={
                                              validationErrors[
                                              `in_progress_reason_${index}_${card.cardId}`
                                              ]
                                            }
                                          />
                                        </div>
                                      ))}
                                    </div>
                                  ) : null}
                                </section>

                                {(task.selectedDevQaCards?.length || task.selectedInProgressCards?.length) ? (
                                  <p className="internal-board-tasks__subtotal internal-board-tasks__subtotal--total">
                                    Total duration: {formatDurationHoursMinutes(getTaskDurationMinutes(task))}
                                  </p>
                                ) : null}
                              </>
                            )}
                            <ErrorText
                              message={"Please select at least one board task."}
                              show={validationErrors[`description_${index}`]}
                            />
                          </div>
                        ) : null}

                        {usesBoardCardsDailyWork(task.selectedProject) ? null : (
                          <div className="mb-4">
                            <div className="form-label">Duration</div>
                            <div className="duration-select-row grid grid-cols-2 gap-3">
                              <div>
                                <div className="form-label text-xs">Hours</div>
                                <Select
                                  value={task.durationHours}
                                  onChange={(value) =>
                                    updateTaskDuration(index, "durationHours", value)
                                  }
                                  options={hourOptions}
                                  placeholder="Hours"
                                  styles={customStyles}
                                  className="react-select-container"
                                  classNamePrefix="react-select"
                                  menuPortalTarget={
                                    typeof document !== "undefined" ? document.body : null
                                  }
                                  menuPosition="fixed"
                                  isDisabled={isReadOnly}
                                />
                              </div>
                              <div>
                                <div className="form-label text-xs">Minutes</div>
                                <Select
                                  value={task.durationMinutes}
                                  onChange={(value) =>
                                    updateTaskDuration(index, "durationMinutes", value)
                                  }
                                  options={getMinuteOptionsForHours(
                                    parseInt(task.durationHours?.value || "0", 10)
                                  )}
                                  placeholder="Minutes"
                                  styles={customStyles}
                                  className="react-select-container"
                                  classNamePrefix="react-select"
                                  menuPortalTarget={
                                    typeof document !== "undefined" ? document.body : null
                                  }
                                  menuPosition="fixed"
                                  isDisabled={isReadOnly}
                                />
                              </div>
                            </div>
                            <ErrorText
                              message={"Please select hours and/or minutes."}
                              show={validationErrors[`duration_${index}`]}
                            />
                          </div>
                        )}

                        {!usesBoardCardsDailyWork(task.selectedProject) ? (
                          <div className="mb-4">
                            <div className="form-label">Task Description</div>
                            {isReadOnly ? (
                              <div
                                className="task-description-readonly"
                                dangerouslySetInnerHTML={{
                                  __html: task.taskDescription,
                                }}
                              />
                            ) : (
                              <ReactQuill
                                theme="snow"
                                value={task.taskDescription}
                                onChange={(value) =>
                                  updateTask(index, "taskDescription", value)
                                }
                                placeholder="Enter task description"
                                readOnly={isReadOnly}
                              />
                            )}
                            <ErrorText
                              message={"Please enter a task description."}
                              show={validationErrors[`description_${index}`]}
                            />
                          </div>
                        ) : null}
                      </div>
                    );
                  })}
                </div>

                <div className="daily-extras-section mt-2">
                  <h3 className="text-lg font-medium mb-3">Tasks</h3>
                  <DailyExtrasQuillField
                    label="In-progress tasks"
                    value={dailyExtras.inProgressTasks}
                    onChange={(value) => updateDailyExtra("inProgressTasks", value)}
                    isReadOnly={isReadOnly}
                    placeholder="Enter in-progress tasks"
                  />
                  <DailyExtrasQuillField
                    label="Planned for tomorrow"
                    value={dailyExtras.plannedForTomorrow}
                    onChange={(value) =>
                      updateDailyExtra("plannedForTomorrow", value)
                    }
                    isReadOnly={isReadOnly}
                    placeholder="Enter planned tasks for tomorrow"
                  />
                </div>

                <div className="daily-extras-section mt-6">
                  <h3 className="text-lg font-medium mb-3">Blockers / Dependencies</h3>
                  <DailyExtrasField
                    label="Issue / Blocker"
                    name="issueBlocker"
                    value={dailyExtras.issueBlocker}
                    onChange={(e) => updateDailyExtra("issueBlocker", e.target.value)}
                    isReadOnly={isReadOnly}
                  />
                  <DailyExtrasField
                    label="Dependency"
                    name="dependency"
                    value={dailyExtras.dependency}
                    onChange={(e) => updateDailyExtra("dependency", e.target.value)}
                    isReadOnly={isReadOnly}
                  />
                  <DailyExtrasField
                    label="Support Required From"
                    name="supportRequiredFrom"
                    value={dailyExtras.supportRequiredFrom}
                    onChange={(e) =>
                      updateDailyExtra("supportRequiredFrom", e.target.value)
                    }
                    isReadOnly={isReadOnly}
                  />
                </div>

                <div className="daily-extras-section mt-6">
                  <h3 className="text-lg font-medium mb-3">Notes / Additional Updates</h3>
                  <DailyExtrasField
                    label="Meetings attended / discussions"
                    name="meetingsDiscussions"
                    value={dailyExtras.meetingsDiscussions}
                    onChange={(e) =>
                      updateDailyExtra("meetingsDiscussions", e.target.value)
                    }
                    isReadOnly={isReadOnly}
                  />
                  <DailyExtrasField
                    label="Client feedback"
                    name="clientFeedback"
                    value={dailyExtras.clientFeedback}
                    onChange={(e) => updateDailyExtra("clientFeedback", e.target.value)}
                    isReadOnly={isReadOnly}
                  />
                  <DailyExtrasField
                    label="Deployment updates or important notes"
                    name="deploymentNotes"
                    value={dailyExtras.deploymentNotes}
                    onChange={(e) =>
                      updateDailyExtra("deploymentNotes", e.target.value)
                    }
                    isReadOnly={isReadOnly}
                  />
                </div>

                {!isReadOnly && !isFullLeaveDay(leaveInfo) && (
                  <div className="flex justify-end mt-4">
                    {createLoading ? (
                      <Loader className="!h-0" />
                    ) : (
                      <AddButton
                        buttonTitle="Submit"
                        className={
                          "!w-full py-2 !flex !items-center !justify-center py-2 !text-xl "
                        }
                        onClick={handleSubmit}
                      />
                    )}
                  </div>
                )}
              </>
            )}


          </div>
        )}
      </Drawer>

      <Modal
        title="Send mail"
        open={showSendMailModal}
        onOk={handleSendViewEmail}
        onCancel={() => {
          if (sendingViewEmail) return;
          resetSendMailDialog();
        }}
        okText={sendingViewEmail ? "Sending…" : "Send email"}
        cancelText="Cancel"
        confirmLoading={sendingViewEmail}
        okButtonProps={{
          disabled:
            sendingViewEmail ||
            !splitDailyHoursNotifyEmails(mailDraft.to).length ||
            !mailDraft.subject.trim() ||
            !mailDraft.body.trim(),
        }}
        cancelButtonProps={{ disabled: sendingViewEmail }}
        width={760}
        className="invoice-mail-preview-modal"
      >
        <div className="invoice-mail-preview">
          {/* {!viewEmployeeNotifyEmail ? (
            <p className="invoice-mail-preview__warning">
              No notification email on file for this employee. Enter recipient addresses in To, or add
              them in Employee Management.
            </p>
          ) : null} */}
          <div className="invoice-mail-preview__meta">
            <div>
              <span className="invoice-mail-preview__label">To</span>
              <input
                className="invoice-mail-preview__input"
                value={mailDraft.to}
                onChange={(event) =>
                  setMailDraft((current) => ({ ...current, to: event.target.value }))
                }
                placeholder="Recipient emails, comma separated"
              />
            </div>
            <div>
              <span className="invoice-mail-preview__label">CC</span>
              <input
                className="invoice-mail-preview__input"
                value={mailDraft.cc}
                onChange={(event) =>
                  setMailDraft((current) => ({ ...current, cc: event.target.value }))
                }
                placeholder="Optional CC emails, comma separated"
              />
            </div>
            <div>
              <span className="invoice-mail-preview__label">BCC</span>
              <input
                className="invoice-mail-preview__input"
                value={mailDraft.bcc}
                onChange={(event) =>
                  setMailDraft((current) => ({ ...current, bcc: event.target.value }))
                }
                placeholder="Optional BCC emails, comma separated"
              />
            </div>
            <div>
              <span className="invoice-mail-preview__label">Subject</span>
              <input
                className="invoice-mail-preview__input"
                value={mailDraft.subject}
                onChange={(event) =>
                  setMailDraft((current) => ({ ...current, subject: event.target.value }))
                }
                placeholder="Email subject"
              />
            </div>
          </div>
          <div className="invoice-mail-preview__body">
            <textarea
              className="invoice-mail-preview__textarea"
              value={mailDraft.body}
              onChange={(event) =>
                setMailDraft((current) => ({ ...current, body: event.target.value }))
              }
              rows={12}
              placeholder="Email message"
            />
          </div>
          <div className="invoice-mail-preview__attachments">
            <span className="invoice-mail-preview__label">Content</span>
            <ul>
              <li>
                Table-formatted daily work updates for {viewEmployeeName} on {date}
              </li>
            </ul>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default ApplyHoursdrawer;
